import { api } from '../hooks/useApi';
import type { ApiCategory, CategoryResponse } from '../types/category';

const CACHE_KEY = 'categories_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CacheData {
  data: ApiCategory[];
  timestamp: number;
}

// Get categories from cache
const getCachedCategories = (): ApiCategory[] | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CacheData = JSON.parse(cached);
    const isExpired = Date.now() - parsed.timestamp > CACHE_DURATION;
    
    if (isExpired) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return parsed.data;
  } catch {
    return null;
  }
};

// Save categories to cache
const setCachedCategories = (data: ApiCategory[]): void => {
  try {
    const cacheData: CacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // Ignore cache errors
  }
};

// Fetch categories from API
export const fetchCategories = async (): Promise<ApiCategory[]> => {
  const response = await api.get<CategoryResponse>('/api/categories/');
  
  if (response.status_code === 200 && response.data?.categories) {
    setCachedCategories(response.data.categories);
    return response.data.categories;
  }
  
  throw new Error(response.message || 'Failed to fetch categories');
};

// Get categories with cache-first strategy
export const getCategories = async (): Promise<ApiCategory[]> => {
  // Return cached data immediately if available
  const cached = getCachedCategories();
  
  // Trigger background refresh
  if (cached) {
    // Silently refresh in background
    fetchCategories().catch(() => {
      // Ignore background refresh errors, use stale cache
    });
    return cached;
  }
  
  // No cache, fetch fresh
  return fetchCategories();
};

// Force fresh fetch (for pull-to-refresh)
export const refreshCategories = async (): Promise<ApiCategory[]> => {
  localStorage.removeItem(CACHE_KEY);
  return fetchCategories();
};

// Transform flat categories to hierarchical structure
export const buildCategoryHierarchy = (categories: ApiCategory[]): {
  parents: ApiCategory[];
  childrenByParent: Record<string, ApiCategory[]>;
} => {
  const parents: ApiCategory[] = [];
  const childrenByParent: Record<string, ApiCategory[]> = {};
  
  // First pass: separate parents and children
  for (const cat of categories) {
    if (cat.level === 0 || !cat.parent) {
      parents.push(cat);
    } else if (cat.parent) {
      if (!childrenByParent[cat.parent]) {
        childrenByParent[cat.parent] = [];
      }
      childrenByParent[cat.parent].push(cat);
    }
  }
  
  // Sort parents by display_order
  parents.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  
  // Sort children within each parent
  for (const parentId in childrenByParent) {
    childrenByParent[parentId].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }
  
  return { parents, childrenByParent };
};
